import { getIdToken } from '../services/firebase';
import { kwApiUrl } from '../config/kwApi';

/**
 * TEMPORARY: skip web sign-in + license gate until Kids Place browser passes Firebase token to WebView.
 * Keyword safety still runs server-side. TODO: set false when Android SSO bridge ships.
 */
export const SEARCH_SKIP_AUTH_AND_LICENSE = true;

export const SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE =
  'Could not verify search safety right now. Try again in a moment.';

/**
 * Classify a failed preflight so UI can distinguish keyword blocks from API outages.
 * @param {{ is_allowed?: boolean, reason?: string } | null | undefined}
 * @returns {'auth_required' | 'license_required' | 'service_error' | 'keyword_blocked' | null}
 */
export function getPreflightFailureKind(preflight) {
  if (!preflight || preflight.is_allowed) return null;
  const reason = preflight.reason || '';
  if (reason === 'auth_required') return 'auth_required';
  if (reason === 'license_required') return 'license_required';
  if (reason === 'service_error') return 'service_error';
  return 'keyword_blocked';
}

/**
 * Server-side license + keyword gate before web search.
 * @param {string} query
 * @returns {Promise<{ is_allowed: boolean, reason: string, search_keyword: string, search_url: string }>}
 */
export async function searchPreflight(query) {
  const q = String(query || '').trim();
  if (!q) {
    return {
      is_allowed: false,
      reason: 'empty_query',
      search_keyword: q,
      search_url: '',
    };
  }

  // Local UI dev only: skip keyword gate when the proxied API is unreachable (e.g. CloudFront 403).
  if (import.meta.env.DEV && import.meta.env.VITE_SEARCH_SKIP_PREFLIGHT === 'true') {
    return {
      is_allowed: true,
      reason: 'ok',
      search_keyword: q,
      search_url: '',
    };
  }

  const token = await getIdToken();
  if (!SEARCH_SKIP_AUTH_AND_LICENSE && !token) {
    return {
      is_allowed: false,
      reason: 'auth_required',
      search_keyword: q,
      search_url: '',
    };
  }

  const url = kwApiUrl('explore/search/preflight');
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ q, lang: 'en' }),
    });

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      console.error(
        'Search preflight returned HTML (wrong API host/path). Expected JSON from Kiddoware API.',
      );
      return {
        is_allowed: false,
        reason: 'service_error',
        search_keyword: q,
        search_url: '',
      };
    }

    const data = await response.json().catch(() => ({}));
    const payload = data?.data && typeof data.data === 'object' ? data.data : data;

    if (!response.ok) {
      return {
        is_allowed: false,
        reason: payload?.reason || 'service_error',
        search_keyword: q,
        search_url: '',
      };
    }

    return {
      is_allowed: payload.allowed === true,
      reason: payload.reason || (payload.allowed ? 'ok' : 'blocked'),
      search_keyword: payload.search_keyword || q,
      search_url: payload.search_url || '',
    };
  } catch (error) {
    console.error('Search preflight failed:', error);
    return {
      is_allowed: false,
      reason: 'service_error',
      search_keyword: q,
      search_url: '',
    };
  }
}

/** @deprecated Use searchPreflight — kept for gradual migration of imports. */
export async function isKeywordAllowed(query) {
  const result = await searchPreflight(query);
  return {
    is_allowed: result.is_allowed,
    search_keyword: result.search_keyword,
    search_url: result.search_url,
    reason: result.reason,
  };
}

export default searchPreflight;
